import { COUNTRY_PAGES } from "@/content/countryPages";
import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Facebook, Globe, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import { type Locale, type Translations } from "@/i18n/translations";

export const Logo = ({ size = 30 }: { size?: number }) => (
  <span className="inline-flex items-center gap-2 font-serif text-[22px] font-semibold text-foreground">
    <img src="/nellove-mark.png" alt="" width={size} height={size} className="object-contain" aria-hidden="true" />
    <span>Nellove</span>
  </span>
);

type SiteLayoutProps = {
  locale: Locale;
  t: Translations;
  /** Path of the equivalent page in the other locale. */
  altPath?: string;
  children: ReactNode;
};

const SiteLayout = ({ locale, t, altPath, children }: SiteLayoutProps) => {
  const navigate = useNavigate();
  const otherLocale: Locale = locale === "en" ? "fr" : "en";

  const switchLocale = (next: Locale) => {
    navigate(altPath ?? `/${next}`);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <a href="#main-content" className="skip-link">
        {t.a11y.skipToContent}
      </a>

      <header className="sticky top-0 z-40 border-b border-stone-200 bg-background">
        <div className="mx-auto flex h-[68px] max-w-6xl items-center justify-between px-5 sm:px-6">
          <a href={`/${locale}`} aria-label="Nellove">
            <Logo size={30} />
          </a>
          <nav className="hidden items-center gap-7 md:flex" aria-label="Primary">
            <a
              href={locale === "fr" ? "/fr/application-mariage" : "/en/wedding-planning-app"}
              className="text-[15px] text-stone-700 transition-colors duration-200 hover:text-rust-600 hover:underline"
            >
              {t.nav.product}
            </a>
            <a
              href={locale === "fr" ? "/fr/pour-organisateurs-de-mariage" : "/en/for-wedding-planners"}
              className="text-[15px] text-stone-700 transition-colors duration-200 hover:text-rust-600 hover:underline"
            >
              {t.nav.planners}
            </a>
            <a
              href={locale === "fr" ? "/fr/checklist-mariage" : "/en/wedding-planning-checklist"}
              className="text-[15px] text-stone-700 transition-colors duration-200 hover:text-rust-600 hover:underline"
            >
              {t.nav.checklist}
            </a>
            <a
              href={`/${locale}/guides`}
              className="text-[15px] text-stone-700 transition-colors duration-200 hover:text-rust-600 hover:underline"
            >
              {t.nav.guides}
            </a>
          </nav>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => switchLocale(otherLocale)}
              className="inline-flex h-10 items-center gap-1.5 rounded-lg px-2.5 text-sm text-stone-700 transition-colors duration-200 hover:bg-rust-50 hover:text-rust-600"
              aria-label={`${t.langSwitch.label}: ${t.langSwitch[otherLocale]}`}
            >
              <Globe className="h-4 w-4" aria-hidden="true" />
              {t.langSwitch[otherLocale]}
            </Button>
            <Button asChild className="hidden h-10 rounded-md px-4 text-sm font-semibold sm:inline-flex">
              <a href={`/${locale}#join`}>{t.nav.join}</a>
            </Button>
          </div>
        </div>
      </header>

      <main id="main-content" className="flex-1">
        {children}
      </main>

      <footer className="border-t border-stone-200 px-5 py-10 sm:px-6">
        <div className="mx-auto flex max-w-6xl flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div>
            <Logo size={28} />
            <p className="mt-3 max-w-[38ch] text-[14px] text-stone-600">{t.footer.tagline}</p>
            <p className="mt-2 max-w-[46ch] text-[13px] text-stone-600">{t.footer.privacy}</p>
          </div>
          <div className="flex flex-col gap-4 md:items-end">
            <div className="flex flex-wrap items-center gap-4 text-[14px] md:justify-end">
              <a
                href={locale === "fr" ? "/fr/application-mariage" : "/en/wedding-planning-app"}
                className="text-rust-600 transition-colors duration-200 hover:underline"
              >
                {t.nav.product}
              </a>
              <a
                href={locale === "fr" ? "/fr/pour-organisateurs-de-mariage" : "/en/for-wedding-planners"}
                className="text-rust-600 transition-colors duration-200 hover:underline"
              >
                {t.nav.planners}
              </a>
              <a
                href={locale === "fr" ? "/fr/organiser-un-mariage-par-pays" : "/en/wedding-planning-by-country"}
                className="text-rust-600 transition-colors duration-200 hover:underline"
              >
                {locale === "fr" ? "Par pays" : "By country"}
              </a>
              {COUNTRY_PAGES.map((p) => (
                <a
                  key={p.id}
                  href={`/${locale}/${p.path[locale]}`}
                  className="text-rust-600 transition-colors duration-200 hover:underline"
                >
                  {p.content[locale].eyebrow}
                </a>
              ))}
              <a
                href={locale === "fr" ? "/fr/alternative-application-mariage" : "/en/wedding-planning-app-alternative"}
                className="text-rust-600 transition-colors duration-200 hover:underline"
              >
                {locale === "fr" ? "Bien choisir" : "Choosing a tool"}
              </a>
              <a
                href={locale === "fr" ? "/fr/checklist-mariage" : "/en/wedding-planning-checklist"}
                className="text-rust-600 transition-colors duration-200 hover:underline"
              >
                {t.nav.checklist}
              </a>
              <a href={`/${locale}/guides`} className="text-rust-600 transition-colors duration-200 hover:underline">
                {t.nav.guides}
              </a>
            </div>
            <div className="flex items-center gap-4 text-[14px]">
              <a
                href="https://www.facebook.com/profile.php?id=61583920627069"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-rust-600 transition-colors duration-200 hover:underline"
              >
                <Facebook className="h-4 w-4" aria-hidden="true" />
                Facebook
              </a>
              <a
                href="https://www.instagram.com/nellove.app/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-rust-600 transition-colors duration-200 hover:underline"
              >
                <Instagram className="h-4 w-4" aria-hidden="true" />
                Instagram
              </a>
            </div>
            <div className="flex items-center gap-4 text-[13px] text-stone-600">
              <Button variant="link" onClick={() => switchLocale(otherLocale)} className="h-auto p-0 text-[13px] font-normal text-stone-600 hover:text-rust-600">
                {t.langSwitch[otherLocale]}
              </Button>
              <span>© 2026 Nellove</span>
              <span>{t.footer.location}</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SiteLayout;
